import type { ActivityGrain, Money, PerformanceDay, PerformanceMeasurement } from '@wms/domain';

// Performans: the periods to pick from and how its figures read. Days are "YYYY-MM-DD" in the zone
// times are shown in (Ayarlar → Saat dilimi); the caller passes today.

const utcDay = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);
const parts = (day: string) => day.split('-').map(Number) as [number, number, number];

/** "2026-10-24" ± days */
export function addDays(day: string, days: number): string {
  const [y, m, d] = parts(day);
  return utcDay(y, m - 1, d + days);
}

/** The first day of the last `months` months up to today: 24 October, 3 months → 25 July (31 May, 3 → 1 March). */
export function monthsBack(today: string, months: number): string {
  const [y, m, d] = parts(today);
  const lastOfMonth = new Date(Date.UTC(y, m - 1 - months + 1, 0)).getUTCDate();
  return utcDay(y, m - 1 - months, Math.min(d, lastOfMonth) + 1);
}

export interface PeriodPreset {
  readonly key: string;
  readonly label: string;
  readonly range: (today: string) => { readonly from: string; readonly to: string };
}

export const PERIOD_PRESETS: readonly PeriodPreset[] = [
  { key: '7', label: 'Son 7 gün', range: (today) => ({ from: addDays(today, -6), to: today }) },
  { key: '30', label: 'Son 30 gün', range: (today) => ({ from: addDays(today, -29), to: today }) },
  { key: 'month', label: 'Bu ay', range: (today) => ({ from: `${today.slice(0, 7)}-01`, to: today }) },
  {
    key: 'last',
    label: 'Geçen ay',
    range: (today) => {
      const [y, m] = parts(today);
      return { from: utcDay(y, m - 2, 1), to: utcDay(y, m - 1, 0) };
    },
  },
  { key: '3m', label: 'Son 3 ay', range: (today) => ({ from: monthsBack(today, 3), to: today }) },
  { key: '12m', label: 'Son 12 ay', range: (today) => ({ from: monthsBack(today, 12), to: today }) },
  { key: 'year', label: 'Bu yıl', range: (today) => ({ from: `${today.slice(0, 4)}-01-01`, to: today }) },
];

/** "%12,5"; "—" without a base */
export const percentText = (rate: number | null) => (rate === null ? '—' : `%${(rate * 100).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);

/** "45 dk", "6,5 sa", "2,3 gün"; "—" without a value */
export function durationText(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} dk`;
  const one = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 1 });
  return hours < 48 ? `${one(hours)} sa` : `${one(hours / 24)} gün`;
}

/** Always in days, for a column to compare: "0,8 gün", "2,4 gün"; "—" without a value */
export const daysText = (hours: number | null) => (hours === null ? '—' : `${(hours / 24).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} gün`);

/** "₺1.234,50", "€10,00" */
export function moneyText(m: Money): string {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: m.currency }).format(Number(m.amount));
  } catch {
    // not a currency code Intl knows
    return `${Number(m.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${m.currency}`;
  }
}

/** Amounts in several currencies side by side; "—" for none */
export const moneyListText = (list: readonly Money[]) => (list.length === 0 ? '—' : list.map(moneyText).join(' + '));

const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'] as const;
const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'] as const;
const dayMonth = (day: string) => `${day.slice(8)}.${day.slice(5, 7)}`;

/** Under a bar: "05.10" for a day or a week (its first day), "Eki ’26" for a month (never read as a day) */
export function bucketLabel(b: { readonly from: string }, grain: ActivityGrain): string {
  return grain === 'month' ? `${MONTHS[Number(b.from.slice(5, 7)) - 1]} ’${b.from.slice(2, 4)}` : dayMonth(b.from);
}

/** The days a bar covers, both ends included */
export const bucketDays = (b: { readonly from: string; readonly to: string }) => Math.round((Date.parse(b.to) - Date.parse(b.from)) / 86_400_000) + 1;

/** A bar the period cuts: only some days of its week or month, so it is shorter for that alone */
export function bucketPartial(b: { readonly from: string; readonly to: string }, grain: ActivityGrain): boolean {
  if (grain === 'day') return false;
  if (grain === 'week') return bucketDays(b) < 7;
  return !(b.from.endsWith('-01') && addDays(b.to, 1).endsWith('-01'));
}

export interface AxisLabel {
  /** "05.10", "Eki" */
  readonly main: string;
  /** under it: the year of a month ("2025", once per year), or the days of a cut bar ("7 gün"); "" for none */
  readonly sub: string;
}

/**
 * The labels under the bars of a period's chart, about twelve of them (null: none under that bar). The
 * last bar, cut by today, always gets one: it says how few days it has.
 */
export function axisLabels(buckets: readonly { readonly from: string; readonly to: string }[], grain: ActivityGrain): (AxisLabel | null)[] {
  const n = buckets.length;
  const every = Math.ceil(n / 12);
  const shown = buckets.map((_, i) => i % every === 0);
  if (n > 1 && !shown[n - 1] && bucketPartial(buckets[n - 1]!, grain)) {
    shown[n - 1] = true;
    // its neighbour gives way
    shown[n - 2] = false;
  }
  let year = '';
  return buckets.map((b, i) => {
    if (!shown[i]) return null;
    const partial = bucketPartial(b, grain);
    const main = grain === 'month' ? MONTHS[Number(b.from.slice(5, 7)) - 1]! : dayMonth(b.from);
    if (partial) return { main, sub: `${bucketDays(b)} gün` };
    if (grain !== 'month' || b.from.slice(0, 4) === year) return { main, sub: '' };
    year = b.from.slice(0, 4);
    return { main, sub: year };
  });
}

/** Under a chart with cut bars: which days they cover ("…: 25.10–31.10.2025 (7 gün), 01.10–24.10.2026 (24 gün)."); null without any */
export function partialBucketsText(buckets: readonly { readonly from: string; readonly to: string }[], grain: ActivityGrain): string | null {
  const cut = buckets.filter((b) => bucketPartial(b, grain));
  if (cut.length === 0) return null;
  const days = cut.map((b) => `${bucketTitle(b, grain)} (${bucketDays(b)} gün)`).join(', ');
  return `${cut.length === 1 ? 'Soluk çubuk' : 'Soluk çubuklar'} ${grain === 'month' ? 'ayın' : 'haftanın'} yalnız dönem içindeki günlerini sayar, bu yüzden kısa görünür: ${days}.`;
}

/**
 * The button under the first rows behind a number. The list holds the newest `limit` rows at most:
 * reaching it, the button says so instead of "all", and `total` (the card's count, when known) tells how many there are.
 */
export function detailMoreText(rows: number, limit: number, total: number | null): { readonly button: string; readonly note: string | null } {
  if (rows < limit) return { button: `Hepsini göster (${rows.toLocaleString('tr-TR')})`, note: null };
  const all = total !== null && total > rows ? ` Bu dönemde toplam ${total.toLocaleString('tr-TR')} kayıt var.` : '';
  return { button: `En yeni ${limit.toLocaleString('tr-TR')} kaydı göster`, note: `Liste en yeni ${limit.toLocaleString('tr-TR')} kayıtla sınırlı.${all}` };
}

/** A bar's days in full: "05.10.2026", "05.10–11.10.2026", "Ekim 2026" (or its days when the period cuts it) */
export function bucketTitle(b: { readonly from: string; readonly to: string }, grain: ActivityGrain): string {
  if (b.from === b.to) return `${dayMonth(b.from)}.${b.from.slice(0, 4)}`;
  const wholeMonth = grain === 'month' && b.from.endsWith('-01') && addDays(b.to, 1).endsWith('-01');
  if (wholeMonth) return `${MONTH_NAMES[Number(b.from.slice(5, 7)) - 1]} ${b.from.slice(0, 4)}`;
  return `${dayMonth(b.from)}${b.from.slice(0, 4) === b.to.slice(0, 4) ? '' : `.${b.from.slice(0, 4)}`}–${dayMonth(b.to)}.${b.to.slice(0, 4)}`;
}

export const GRAIN_TEXT: Readonly<Record<ActivityGrain, string>> = { day: 'Gün gün', week: 'Hafta hafta', month: 'Ay ay' };

/** Monday of a day's week */
function weekStart(day: string): string {
  return addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7));
}

/** The days of the Performans report added up per week (from Monday) or month, from the period's first day to its last. */
export function groupDays(days: readonly PerformanceDay[], grain: ActivityGrain): (PerformanceDay & { readonly to: string })[] {
  const groups = new Map<string, PerformanceDay & { to: string }>();
  for (const d of days) {
    const key = grain === 'day' ? d.date : grain === 'week' ? weekStart(d.date) : d.date.slice(0, 7);
    const seen = groups.get(key);
    groups.set(
      key,
      seen
        ? {
            date: seen.date,
            to: d.date,
            due: seen.due + d.due,
            onTime: seen.onTime + d.onTime,
            packed: seen.packed + d.packed,
            packedClean: seen.packedClean + d.packedClean,
            linesCounted: seen.linesCounted + d.linesCounted,
            linesMatched: seen.linesMatched + d.linesMatched,
          }
        : { ...d, to: d.date },
    );
  }
  return [...groups.values()];
}

// --- Ölçüm başlangıcı ("Performansı sıfırla") ------------------------------------------------------

/** The time a start is chosen in: Türkiye's, as the warehouse's clocks and the marketplaces show it. */
export const START_ZONE = 'Europe/Istanbul';

/** A moment as a date-and-time field shows it in `zone`: "2026-10-24T21:30" */
export function wallClock(moment: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(moment);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/** The moment (ISO) a date and time of a field ("2026-10-24T21:30") stands for in `zone`; null for anything else. */
export function zonedToIso(local: string, zone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return null;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  // 31 February and 25 o'clock are no times
  if (new Date(asUtc).toISOString().slice(0, 16) !== local) return null;
  // the zone's offset at that moment; once more in case a clock change lies between
  let moment = asUtc;
  for (let i = 0; i < 2; i++) moment += asUtc - Date.parse(`${wallClock(new Date(moment), zone)}:00Z`);
  return new Date(moment).toISOString();
}

/** The line above the figures while a start is in effect (null: none); `stamp` writes a moment ("24.10.2026 21:30"). */
export function measurementText(m: PerformanceMeasurement, stamp: (iso: string) => string): string | null {
  // a seller in warehouses that count from different moments: each one's
  const started = m.warehouses.filter((w) => w.since !== null);
  if (started.length > 0 && m.warehouses.some((w) => w.since !== started[0]!.since)) {
    const each = started.map((w) => `${w.warehouseCode} deposunda ${stamp(w.since!)}`).join(', ');
    const rest = m.warehouses.filter((w) => w.since === null).map((w) => w.warehouseCode);
    const whole = rest.length === 0 ? '' : `; ${rest.join(', ')} ${rest.length > 1 ? 'depolarında' : 'deposunda'} tüm geçmiş sayılıyor`;
    return `Ölçüm başlangıcı: ${each} — öncesi sayılmıyor${whole}`;
  }
  if (m.since) return `Ölçüm başlangıcı: ${stamp(m.since)} — öncesi sayılmıyor`;
  if (m.sellers.length > 0) return 'Bazı satıcıların kendi ölçüm başlangıcı var — öncesi sayılmıyor';
  return null;
}
